import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CoverImg } from "@/components/ui/CoverImg";
import { IconMic, IconMicOff, IconNext, IconPause, IconPlay, IconPrev } from "@/components/ui/icons";
import { api } from "@/lib/api";
import { coverUrl } from "@/lib/cover";
import { sfx } from "@/lib/sfx";
import {
  FONT, canvasToBlob, drawCover, loadImage, roundRect, shareOrDownload, wrapLines,
} from "@/lib/shareImage";
import { useCoverColor } from "@/hooks/useCoverColor";
import { usePlayer } from "@/hooks/usePlayer";
import { usePlayerProgress } from "@/hooks/usePlayerProgress";
import type { Track } from "@/types/types";

type Line = { t: number; text: string };
type LyricsResp = {
  found: boolean;
  synced: boolean;
  lines: Line[];
  plain: string;
  instrumental?: boolean;
};

const MAX_SHARE = 5;

/** Mic button + Spotify-style full-screen lyrics: cover-coloured backdrop,
 *  big lines, tap a line to jump there, pick lines to share as an image. */
export function KaraokeView() {
  const [open, setOpen] = useState(false);
  const { current } = usePlayer();
  const [data, setData] = useState<LyricsResp | null>(null);
  const [loading, setLoading] = useState(false);

  // fetch on every track change (not gated by `open`) so the mic button
  // itself can show "no lyrics" without requiring the user to open the
  // karaoke view first
  useEffect(() => {
    if (!current?.id) { setData(null); return; }
    let cancelled = false;
    setLoading(true); setData(null);
    api<LyricsResp>(`/api/lyrics/${current.id}`)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData({ found: false, synced: false, lines: [], plain: "" }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [current?.id]);

  const noLyrics = !!data && (!data.found || !!data.instrumental);

  return (
    <>
      <button title={noLyrics ? "Pas de paroles pour ce morceau" : "Paroles"}
              onClick={() => { sfx.swoosh(); setOpen(true); }}>
        {noLyrics ? <IconMicOff /> : <IconMic />}
      </button>
      {open && current && createPortal(
        <LyricsScreen track={current} data={data} loading={loading}
                      onClose={() => { sfx.reverseSwoosh(); setOpen(false); }} />,
        document.body,
      )}
    </>
  );
}

function LyricsScreen({ track, data, loading, onClose }: {
  track: Track; data: LyricsResp | null; loading: boolean; onClose: () => void;
}) {
  const { playing, toggle, next, prev, seek } = usePlayer();
  const progress = usePlayerProgress();
  const color = useCoverColor(track.album?.cover_id) || "#5b3d8f";
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLDivElement | null>(null);
  const [picking, setPicking] = useState(false);
  const [picked, setPicked] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const on = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", on);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", on); document.body.style.overflow = ""; };
  }, [onClose]);

  // a new song invalidates any line selection
  useEffect(() => { setPicked([]); setPicking(false); }, [track.id]);

  const synced = !!data?.synced && (data?.lines.length ?? 0) > 0;
  const lines: Line[] = useMemo(() => {
    if (!data) return [];
    if (synced) return data.lines;
    return (data.plain || "").split("\n").map((text) => ({ t: -1, text }));
  }, [data, synced]);

  let activeIdx = -1;
  if (synced) {
    for (let i = 0; i < lines.length; i++) {
      if (lines[i].t <= progress) activeIdx = i;
      else break;
    }
  }

  useEffect(() => {
    if (picking || activeIdx < 0 || !activeRef.current || !scrollerRef.current) return;
    const el = activeRef.current;
    const scroller = scrollerRef.current;
    scroller.scrollTo({ top: el.offsetTop - scroller.clientHeight * 0.38, behavior: "smooth" });
  }, [activeIdx, picking]);

  const onLine = (i: number) => {
    if (picking) {
      if (!lines[i].text.trim()) return;
      setPicked((p) => p.includes(i) ? p.filter((x) => x !== i)
        : p.length >= MAX_SHARE ? p : [...p, i].sort((a, b) => a - b));
      return;
    }
    if (synced && lines[i].t >= 0) { sfx.tap(); seek(lines[i].t); }
  };

  const makeCard = useCallback(async () => {
    if (!picked.length) return;
    setBusy(true);
    try {
      const W = 1080, H = 1350;
      const c = document.createElement("canvas");
      c.width = W; c.height = H;
      const ctx = c.getContext("2d")!;
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, color); g.addColorStop(1, "#0b0b0f");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

      const card = { x: 90, y: 150, w: W - 180, h: H - 330 };
      ctx.fillStyle = color;
      roundRect(ctx, card.x, card.y, card.w, card.h, 36); ctx.fill();
      ctx.fillStyle = "rgba(0,0,0,.18)";
      roundRect(ctx, card.x, card.y, card.w, card.h, 36); ctx.fill();

      const img = await loadImage(coverUrl(track.album?.cover_id, 300));
      drawCover(ctx, img, card.x + 60, card.y + 60, 120, 14);
      ctx.fillStyle = "#fff";
      ctx.font = `800 40px ${FONT}`;
      ctx.fillText(clip(ctx, track.title, card.w - 260), card.x + 210, card.y + 112);
      ctx.fillStyle = "rgba(255,255,255,.8)";
      ctx.font = `500 32px ${FONT}`;
      ctx.fillText(clip(ctx, track.artist?.name ?? "", card.w - 260), card.x + 210, card.y + 160);

      const text = picked.map((i) => lines[i].text).join("\n");
      let size = 64;
      let wrapped: string[] = [];
      for (; size >= 36; size -= 4) {
        ctx.font = `800 ${size}px ${FONT}`;
        wrapped = wrapLines(ctx, text, card.w - 120);
        if (wrapped.length * size * 1.25 <= card.h - 300) break;
      }
      ctx.fillStyle = "#fff";
      let y = card.y + 270;
      for (const l of wrapped) { ctx.fillText(l, card.x + 60, y); y += size * 1.25; }

      ctx.fillStyle = "rgba(255,255,255,.85)";
      ctx.font = `800 40px ${FONT}`;
      ctx.fillText("slowson", 90, H - 90);
      const blob = await canvasToBlob(c);
      if (blob) await shareOrDownload(blob, `paroles-${slug(track.title)}.png`, `${track.title} — ${track.artist?.name ?? ""}`);
      setPicking(false); setPicked([]);
    } finally { setBusy(false); }
  }, [picked, lines, color, track]);

  return (
    <div className="lyrics-screen" role="dialog" aria-modal="true"
         style={{ background: `linear-gradient(180deg, ${color} 0%, color-mix(in srgb, ${color} 55%, #000) 100%)` }}>
      <header className="lyrics-head">
        <CoverImg coverId={track.album?.cover_id} alt={track.title} size={44} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="trunc" style={{ fontWeight: 800 }}>{track.title}</div>
          <div className="trunc" style={{ opacity: .8, fontSize: 13 }}>{track.artist?.name}</div>
        </div>
        {lines.some((l) => l.text.trim()) && (
          <button className={"lyrics-chip" + (picking ? " on" : "")}
                  onClick={() => { sfx.tap(); setPicking((v) => !v); setPicked([]); }}>
            {picking ? "Annuler" : "Partager"}
          </button>
        )}
        <button className="lyrics-close" onClick={onClose} aria-label="Fermer">✕</button>
      </header>

      {picking && (
        <div className="lyrics-hint">Choisis jusqu'à {MAX_SHARE} lignes à partager</div>
      )}

      <div ref={scrollerRef} className="lyrics-scroll">
        {loading && <div className="lyrics-msg">Chargement des paroles…</div>}
        {!loading && data?.instrumental && <div className="lyrics-msg">🎶 Morceau instrumental</div>}
        {!loading && data && !data.found && <div className="lyrics-msg">Pas de paroles trouvées pour ce morceau.</div>}
        {!loading && lines.map((l, i) => {
          const state = picking
            ? (picked.includes(i) ? "picked" : "future")
            : !synced ? "plain" : i === activeIdx ? "active" : i < activeIdx ? "past" : "future";
          return (
            <div key={i} ref={i === activeIdx ? activeRef : undefined}
                 className={`lyrics-line ${state}`} onClick={() => onLine(i)}>
              {l.text || (synced ? "♪" : " ")}
            </div>
          );
        })}
        <div style={{ height: "40vh" }} />
      </div>

      <footer className="lyrics-foot">
        {picking ? (
          <button className="btn primary" disabled={!picked.length || busy} onClick={() => void makeCard()}>
            {busy ? "Création…" : `Créer l'image (${picked.length})`}
          </button>
        ) : (
          <>
            <button aria-label="Précédent" onClick={() => { sfx.tap(); prev(); }}><IconPrev size={26} /></button>
            <button aria-label={playing ? "Pause" : "Lecture"} className="lyrics-play"
                    onClick={() => { sfx.pop(); toggle(); }}>
              {playing ? <IconPause size={26} /> : <IconPlay size={26} />}
            </button>
            <button aria-label="Suivant" onClick={() => { sfx.tap(); next(); }}><IconNext size={26} /></button>
          </>
        )}
      </footer>
    </div>
  );
}

function clip(ctx: CanvasRenderingContext2D, s: string, max: number): string {
  if (ctx.measureText(s).width <= max) return s;
  let out = s;
  while (out.length > 1 && ctx.measureText(out + "…").width > max) out = out.slice(0, -1);
  return out + "…";
}

function slug(s: string): string {
  return (s || "titre").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
}
